import { useState } from "react";
import { useTickets } from "@/hooks/useTickets";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Ticket, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  ArrowUpCircle,
  ArrowRightCircle,
  Filter,
  Plus
} from "lucide-react";

const TicketManagement = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRoles(user?.id);
  const { tickets, feedbackSubmissions, createTicket, updateTicket, convertFeedbackToTicket } = useTickets();
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedComponent, setSelectedComponent] = useState<string>('all');
  const [selectedPage, setSelectedPage] = useState<string>('all');
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'feedback' as const,
    priority: 'medium' as const,
    component_affected: '',
    page_affected: ''
  });

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You need admin privileges to access ticket management.</p>
        </CardContent>
      </Card>
    );
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <ArrowUpCircle className="w-4 h-4 text-red-500" />;
      case 'high': return <ArrowUpCircle className="w-4 h-4 text-orange-500" />;
      case 'medium': return <ArrowRightCircle className="w-4 h-4 text-yellow-500" />;
      case 'low': return <ArrowRightCircle className="w-4 h-4 text-green-500" />;
      default: return null;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'in_progress': return <ArrowRightCircle className="w-4 h-4 text-orange-500" />;
      case 'resolved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'closed': return <CheckCircle className="w-4 h-4 text-gray-500" />;
      default: return null;
    }
  };

  const filteredTickets = tickets?.filter(ticket => {
    const statusMatch = selectedStatus === 'all' || ticket.status === selectedStatus;
    const priorityMatch = selectedPriority === 'all' || ticket.priority === selectedPriority;
    const componentMatch = selectedComponent === 'all' || ticket.component_affected === selectedComponent;
    const pageMatch = selectedPage === 'all' || ticket.page_affected === selectedPage;
    return statusMatch && priorityMatch && componentMatch && pageMatch;
  });

  const handleCreateTicket = async () => {
    try {
      await createTicket.mutateAsync({
        ...newTicket,
        created_by: user?.id
      });
      setNewTicket({ title: '', description: '', category: 'feedback', priority: 'medium', component_affected: '', page_affected: '' });
      toast.success("Ticket created successfully!");
    } catch (error) {
      toast.error("Failed to create ticket");
    }
  };

  const handleUpdateTicketStatus = async (ticketId: string, status: string) => {
    try {
      await updateTicket.mutateAsync({
        id: ticketId,
        updates: { 
          status: status as any,
          resolved_at: status === 'resolved' ? new Date().toISOString() : null
        }
      });
      toast.success("Ticket updated successfully!");
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  const handleConvertFeedback = async (feedbackId: string) => {
    try {
      await convertFeedbackToTicket.mutateAsync(feedbackId);
      toast.success("Feedback converted to ticket successfully!");
    } catch (error) {
      toast.error("Failed to convert feedback");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ticket Management</h1>
          <p className="text-muted-foreground">Manage user feedback and development tickets</p>
        </div>
      </div>

      <Tabs defaultValue="tickets" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tickets">
            <Ticket className="w-4 h-4 mr-2" />
            Tickets ({tickets?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="feedback">
            <MessageSquare className="w-4 h-4 mr-2" />
            Feedback ({feedbackSubmissions?.filter(f => f.status === 'pending').length || 0})
          </TabsTrigger>
          <TabsTrigger value="create">
            <Plus className="w-4 h-4 mr-2" />
            Create Ticket
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-4">
          <div className="flex gap-4 items-center flex-wrap">
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedPriority} onValueChange={setSelectedPriority}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedComponent} onValueChange={setSelectedComponent}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by component" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Components</SelectItem>
                {[...new Set(tickets?.map(t => t.component_affected).filter(Boolean))].map(component => (
                  <SelectItem key={component} value={component!}>{component}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedPage} onValueChange={setSelectedPage}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pages</SelectItem>
                {[...new Set(tickets?.map(t => t.page_affected).filter(Boolean))].map(page => (
                  <SelectItem key={page} value={page!}>{page}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4">
            {filteredTickets?.map((ticket) => (
              <Card key={ticket.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{ticket.title}</CardTitle>
                      <CardDescription className="mt-1">
                        {ticket.description}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {getPriorityIcon(ticket.priority)}
                      {getStatusIcon(ticket.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline">{ticket.category}</Badge>
                      <Badge variant="secondary">{ticket.priority}</Badge>
                      <Badge>{ticket.status}</Badge>
                      {ticket.component_affected && (
                        <Badge variant="outline">📦 {ticket.component_affected}</Badge>
                      )}
                      {ticket.page_affected && (
                        <Badge variant="outline">📄 {ticket.page_affected}</Badge>
                      )}
                    </div>
                    <Select
                      value={ticket.status}
                      onValueChange={(status) => handleUpdateTicketStatus(ticket.id, status)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {ticket.created_at && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Created: {new Date(ticket.created_at).toLocaleDateString()}
                      {ticket.created_by && ` by ${ticket.created_by.substring(0, 8)}...`}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="feedback" className="space-y-4">
          <div className="grid gap-4">
            {feedbackSubmissions?.filter(f => f.status === 'pending').map((feedback) => (
              <Card key={feedback.id}>
                <CardHeader>
                  <CardTitle className="text-lg">{feedback.title}</CardTitle>
                  <CardDescription>{feedback.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <Badge variant="outline">{feedback.category}</Badge>
                      <Badge variant="secondary">{feedback.priority}</Badge>
                    </div>
                    <Button
                      onClick={() => handleConvertFeedback(feedback.id)}
                      size="sm"
                    >
                      Convert to Ticket
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Submitted: {new Date(feedback.submitted_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="create" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create New Ticket</CardTitle>
              <CardDescription>Create a new ticket manually</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Ticket title"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
              />
              <Textarea
                placeholder="Ticket description"
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Select
                  value={newTicket.category}
                  onValueChange={(value) => setNewTicket({ ...newTicket, category: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feedback">Feedback</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={newTicket.priority}
                  onValueChange={(value) => setNewTicket({ ...newTicket, priority: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Component affected (optional)"
                  value={newTicket.component_affected}
                  onChange={(e) => setNewTicket({ ...newTicket, component_affected: e.target.value })}
                />
                <Input
                  placeholder="Page affected (optional)"
                  value={newTicket.page_affected}
                  onChange={(e) => setNewTicket({ ...newTicket, page_affected: e.target.value })}
                />
              </div>
              <Button
                onClick={handleCreateTicket}
                disabled={!newTicket.title || !newTicket.description}
                className="w-full"
              >
                Create Ticket
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TicketManagement;